import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Button,
  Card,
  CardContent,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import { useRecoilState } from "recoil";
import { platformState } from "../global-state";
import MSwitch from "../common/switch";

const NOTIFICATION_SETTINGS_KEY = "notification_settings";

const getNotificationSettings = () => {
  const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
  return saved
    ? JSON.parse(saved)
    : {
        desktopNotificationEnabled: false,
        notificationPermission: "default",
      };
};

const saveNotificationSettings = (settings) => {
  localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
};

const DesktopNotificationSettingsView = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const history = useHistory();
  const { successToast, warningToast, errorToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [platform] = useRecoilState(platformState);

  const [settings, setSettings] = useState(getNotificationSettings());

  // Show message if not desktop or app
  if (platform.source !== "desktop" && platform.source !== "app") {
    return (
      <Box
        display="flex"
        justifyContent="start"
        alignItems="start"
        flexDirection="column"
        height="100%"
        p={2}
        width="100%"
      >
        <Box
          position="static"
          sx={{
            display: "flex",
            alignItems: "center",
            justfyContent: "flex-start",
            mb: 2,
          }}
        >
          {isSmallScreen && (
            <IconButton
              color="inherit"
              edge="start"
              sx={{ mr: 1 }}
              onClick={() => {
                history.replace("/setting");
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box sx={{ display: "flex" }}>
            <Typography variant="h6" style={{ textAlign: "center" }}>
              Notification Settings
            </Typography>
          </Box>
        </Box>
        <Card
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: "8px",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              System notifications are not available for web platform.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  useEffect(() => {
    const checkNotificationPermission = async () => {
      if ("Notification" in window) {
        const permission = await window.Notification.requestPermission();
        setSettings((prev) => ({
          ...prev,
          notificationPermission: permission,
        }));
        saveNotificationSettings({
          ...settings,
          notificationPermission: permission,
        });
      }
    };

    if (settings.desktopNotificationEnabled) {
      checkNotificationPermission();
    }
  }, [settings.desktopNotificationEnabled]);

  const handleNotificationChange = async (event) => {
    const enabled = event.target.checked;

    if (enabled && "Notification" in window) {
      try {
        const permission = await window.Notification.requestPermission();
        if (permission === "granted") {
          setSettings((prev) => ({
            ...prev,
            desktopNotificationEnabled: enabled,
            notificationPermission: permission,
          }));
          saveNotificationSettings({
            ...settings,
            desktopNotificationEnabled: enabled,
            notificationPermission: permission,
          });
          successToast("Notifications enabled successfully");
        } else {
          warningToast("Notification permission was not granted");
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        errorToast("Failed to request notification permission");
      }
    } else {
      setSettings((prev) => ({
        ...prev,
        desktopNotificationEnabled: enabled,
      }));
      saveNotificationSettings({
        ...settings,
        desktopNotificationEnabled: enabled,
      });
      if (enabled) {
        successToast("Notifications enabled successfully");
      } else {
        successToast("Notifications disabled");
      }
    }
  };

  const sendTestNotification = () => {
    const notification = {
      title: "Test Notification",
      message: "This is a test notification",
      timestamp: new Date().toISOString(),
    };
    if (
      "Notification" in window &&
      settings.notificationPermission === "granted"
    ) {
      new window.Notification(notification.title, {
        body: notification.message,
        icon: "/logo512.png",
      });
      successToast("Test notification sent");
    } else {
      errorToast("Notification permission not granted");
    }
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box
          display="flex"
          justifyContent="start"
          alignItems="start"
          flexDirection="column"
          height="100%"
          p={2}
          width="100%"
        >
          <Box
            position="static"
            sx={{
              display: "flex",
              alignItems: "center",
              justfyContent: "flex-start",
              mb: 2,
            }}
          >
            {isSmallScreen && (
              <IconButton
                color="inherit"
                edge="start"
                sx={{ mr: 1 }}
                onClick={() => {
                  history.replace("/setting");
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ display: "flex" }}>
              <Typography variant="h6" style={{ textAlign: "center" }}>
                Notification Settings
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <Card
              sx={{
                width: "100%",
                backgroundColor: "background.paper",
                borderRadius: "8px",
                boxShadow: "none",
              }}
            >
              <CardContent>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Pop-up Notifications
                  </Typography>

                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Enable notifications to receive important alerts from the
                      application.
                    </Typography>

                    <FormControlLabel
                      control={
                        <MSwitch
                          checked={settings.desktopNotificationEnabled}
                          onChange={handleNotificationChange}
                          color="primary"
                          disabled={
                            settings.notificationPermission === "denied"
                          }
                        />
                      }
                      label="Enable System Pop-up Notifications"
                      sx={{
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        pl: 2,
                        mb: 1,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          marginLeft: 2,
                        },
                      }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={sendTestNotification}
                        sx={{
                          textTransform: "none",
                          pl: 2,
                        }}
                        disabled={!settings.desktopNotificationEnabled}
                      >
                        Send Test Notification
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </>
  );
};

export default DesktopNotificationSettingsView;
